import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

const formatBytes = (bytes) => {
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// POC: stream is proxied through this app's own nginx at /cctv-hls/ (see
// nginx.conf), which gates access with auth_request against the same
// gateway endpoint App.js already uses. Relative paths so this works on
// whatever domain the page is loaded from.
//
// "-h264" paths: the cameras only output H265/HEVC, which Chrome/Firefox
// can't decode in-browser — cctv-transcode-cam{1,2} (ffmpeg + VAAPI on the
// S04 iGPU) re-encodes to H264 and republishes into cctv-gateway under
// these path names. Point the player at the transcoded output, not the
// camera's raw H265 path.
const CAMERAS = [
    { path: 'cam1-h264', label: 'Camera 1 — .152' },
    { path: 'cam2-h264', label: 'Camera 2 — .151' }
];

const getAuthToken = () => {
    try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        return user.token || '';
    } catch {
        return '';
    }
};

const CameraPlayer = ({ cam }) => {
    const videoRef = useRef(null);
    const [bytesLoaded, setBytesLoaded] = useState(0);
    const [hasImage, setHasImage] = useState(false);

    useEffect(() => {
        const video = videoRef.current;
        if (!video) return;

        const src = `/cctv-hls/${cam.path}/index.m3u8`;
        const onPlaying = () => setHasImage(true);
        video.addEventListener('playing', onPlaying);

        if (Hls.isSupported()) {
            const hls = new Hls({
                xhrSetup: (xhr) => {
                    const token = getAuthToken();
                    if (token) xhr.setRequestHeader('Authorization', 'Bearer ' + token);
                }
            });
            hls.loadSource(src);
            hls.attachMedia(video);
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
                video.play().catch(() => {});
            });
            // Running total of fragment bytes downloaded so far — only
            // meaningful to show while there's no image yet (see render below).
            hls.on(Hls.Events.FRAG_LOADED, (event, data) => {
                setBytesLoaded((prev) => prev + (data.frag.stats.loaded || 0));
            });

            return () => {
                hls.destroy();
                video.removeEventListener('playing', onPlaying);
            };
        }

        if (video.canPlayType('application/vnd.apple.mpegurl')) {
            // Safari native HLS — xhrSetup auth header trick doesn't apply here,
            // so playback will 401 against this auth-gated proxy. Not solving
            // Safari in this pass.
            video.src = src;
        }

        return () => video.removeEventListener('playing', onPlaying);
    }, [cam.path]);

    const enterFullscreen = () => {
        const video = videoRef.current;
        if (video && video.requestFullscreen) video.requestFullscreen();
    };

    return (
        <div style={{ position: 'relative', width: '640px' }}>
            <span style={{
                position: 'absolute', top: '6px', left: '6px', zIndex: 1,
                background: 'rgba(0,0,0,0.6)', color: '#ff4444', fontSize: '11px',
                fontWeight: 'bold', padding: '2px 6px', borderRadius: '3px'
            }}>
                ● LIVE{!hasImage && bytesLoaded > 0 ? ` — loading ${formatBytes(bytesLoaded)}` : ''}
            </span>
            <button
                onClick={enterFullscreen}
                title="Fullscreen"
                style={{
                    position: 'absolute', bottom: '6px', right: '6px', zIndex: 1,
                    background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none',
                    borderRadius: '3px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer'
                }}
            >⛶</button>
            {/* No native controls — a seek bar implies scrub-back/VOD, which this
                isn't (HLS live edge only). Autoplay muted, no UI to fake pause/rewind. */}
            <video ref={videoRef} muted autoPlay playsInline width="640" height="360" />
        </div>
    );
};

const CctvViewer = () => {
    return (
        <div style={{ padding: '20px' }}>
            <h2>CCTV Live View (POC)</h2>
            <p style={{ color: '#666', fontSize: '13px' }}>
                Requires login (uses your session token) — may take a few seconds to start, not real-time (HLS).
                Safari native HLS is not authenticated in this POC — use Chrome/Firefox.
            </p>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
                {CAMERAS.map(cam => (
                    <div key={cam.path} style={{ border: '1px solid #ddd', borderRadius: '4px', padding: '10px' }}>
                        <div style={{ fontWeight: 'bold', marginBottom: '8px' }}>{cam.label}</div>
                        <CameraPlayer cam={cam} />
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CctvViewer;
